"use client";
import { useState, useEffect, useCallback } from "react";

/**
 * Custom hook to track window width with proper SSR handling and debouncing
 * @param debounceMs - Debounce delay in milliseconds (default: 150ms)
 * @returns {number} Current window width in pixels
 */
function useWindowWidth(debounceMs: number = 150) {
  // Helper function to get window width safely
  const getWidth = useCallback(() => {
    return typeof window !== "undefined" ? window.innerWidth : 0;
  }, []);

  // Initialize state - will be 0 during SSR, updated after hydration
  const [width, setWidth] = useState(0);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    // Mark as client-side and set initial width
    setIsClient(true);
    setWidth(getWidth());
  }, [getWidth]);

  useEffect(() => {
    // Skip if not on client side
    if (!isClient || typeof window === "undefined") return;

    let timeoutId: NodeJS.Timeout;
    let rafId: number;

    // Debounced resize handler with RAF for better performance
    const handleResize = () => {
      clearTimeout(timeoutId);

      timeoutId = setTimeout(() => {
        // Use requestAnimationFrame for smooth updates
        rafId = requestAnimationFrame(() => {
          const newWidth = window.innerWidth;
          setWidth((prevWidth) => {
            // Only update if width actually changed to prevent unnecessary re-renders
            return prevWidth !== newWidth ? newWidth : prevWidth;
          });
        });
      }, debounceMs);
    };

    // Add event listener with passive option for better performance
    window.addEventListener("resize", handleResize, { passive: true });

    // Cleanup function
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeoutId);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [isClient, debounceMs]);

  return width;
}

// Alternative hook with breakpoint utilities
function useResponsiveWidth(
  breakpoints = { sm: 640, md: 768, lg: 1024, xl: 1280 }
) {
  const width = useWindowWidth();

  const getBreakpoint = useCallback(() => {
    if (width === 0) return "unknown"; // During SSR or initial load
    if (width < breakpoints.sm) return "xs";
    if (width < breakpoints.md) return "sm";
    if (width < breakpoints.lg) return "md";
    if (width < breakpoints.xl) return "lg";
    return "xl";
  }, [width, breakpoints]);

  return {
    width,
    breakpoint: getBreakpoint(),
    isXs: width > 0 && width < breakpoints.sm,
    isSm: width >= breakpoints.sm && width < breakpoints.md,
    isMd: width >= breakpoints.md && width < breakpoints.lg,
    isLg: width >= breakpoints.lg && width < breakpoints.xl,
    isXl: width >= breakpoints.xl,
    isMobile: width > 0 && width < breakpoints.md,
    isTablet: width >= breakpoints.md && width < breakpoints.lg,
    isDesktop: width >= breakpoints.lg,
    isReady: width > 0, // Indicates if the hook has initialized
  };
}

// Example usage with TypeScript support
/*
interface WindowWidthComponentProps {
  breakpoint?: number;
}

function WindowWidthComponent({ breakpoint = 768 }: WindowWidthComponentProps) {
  const { width, isMobile, isReady } = useResponsiveWidth();
  
  // Prevent hydration mismatch by not rendering responsive content until ready
  if (!isReady) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <p>Window width: {width}px</p>
      <p>Device type: {isMobile ? 'Mobile' : 'Desktop'}</p>
    </div>
  );
}
*/

export default useWindowWidth;
export { useResponsiveWidth };
